export enum UploadStatus {
  Waiting = 'Waiting',
  Uploading = 'Uploading',
  Complete = 'Complete',
  Failed = 'Failed',
  Cancelled = 'Cancelled',
  Interrupted = 'Interrupted'
}

export interface ProgressEntry {
  range: Range;
  duration: number;
}

export interface UploadSession {
  id: string;
  name: string;
  size: number;
  status: UploadStatus;
  url: string;
  created: Date;
  expiration: Date;
  meta?: Record<string, any>;
  retryCount?: number;
  progress?: ProgressEntry[];
  result?: any;
}

export interface Range {
  start: number;
  end: number;
}

export interface UploadProviderOptions {
  chunkSize: number;
  /**
   * The threshold at which we should start chunking the file.
   * This could be anything, but we'll use 10MB as a default.
   *  - Set to Infinity to disable chunking (if there isn't an option in your given uploader).
   *  - Set to 0 to always chunk (if there isn't an option in your given uploader).
   */
  chunkedUploadThreshold: number;
}

export interface Chunk {
  offset: number;
  data: ArrayBuffer;
}

export const readFileChunk = async (file: File, start: number, end: number) => {
  const slice = file.slice(start, end);
  const data = await blobToArrayBuffer(slice);
  const chunk: Chunk = { offset: start, data };
  return chunk;
};

export function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = reject;
    reader.readAsArrayBuffer(blob);
  });
}

export interface ReadFileInChunksOptions {
  chunkSize: number;
  initialOffset?: number;
}

export async function* readFileInChunks(
  file: File,
  { chunkSize, initialOffset = 0 }: ReadFileInChunksOptions
): AsyncGenerator<Chunk> {
  let offset = initialOffset;
  while (offset < file.size) {
    const chunk = await readFileChunk(file, offset, offset + chunkSize);
    yield chunk;
    offset += chunkSize;
  }
}

export type Upload = Readonly<[UploadSession, File]>;

export function* queuedUploads(uploads: Upload[]) {
  for (const upload of uploads) {
    yield upload;
  }
}
